/**
 * P&L tab for v2.
 *
 * Structurally different from v1's: cost of sales and every indirect cost
 * group now come from their own tabs rather than being entered here, so most
 * rows are computed and the editable ones are the lines the template genuinely
 * treats as P&L inputs.
 *
 * Cost of Sales is NEGATIVE and includes direct labour (spec 7.1), so Gross
 * Profit is revenue PLUS cost of sales.
 */

import { useCallback } from 'react';
import type { PnlLineComputed } from '../../../types/financial-model-v2';
import { ComputedDataRow } from '../table/DataRow';
import { V2EditableRow } from './V2EditableRow';
import { GrandTotalRow } from '../table/GrandTotalRow';
import { SectionHeader } from '../table/SectionHeader';
import { SubtotalRow } from '../table/SubtotalRow';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import { V2Sheet } from './V2Table';


/**
 * Declared profit and loss lines for one placement.
 *
 * The template names twelve cost buckets and everything else used to be
 * folded into "other admin". These are the lines IAS 1 asks for where they
 * aid understanding - restructuring, research, grants - and the by-nature
 * captions a filing may use instead of the functions this model assumes.
 *
 * They are already inside the subtotals; showing them is what lets a reader
 * see what a subtotal is made of.
 */
function DeclaredPnlRows({
  lines,
  placement,
}: {
  lines: Record<string, PnlLineComputed> | undefined;
  placement: string;
}) {
  const updateInput = useFinancialModelV2Store((s) => s.updateInput);
  const rows = Object.entries(lines ?? {}).filter(
    ([, line]) =>
      line.placement === placement &&
      Object.values(line.values ?? {}).some((v) => v),
  );
  if (rows.length === 0) return null;
  return (
    <>
      {rows.map(([key, line]) => (
        // A line the filing declared: its figures are entered, as the Sales lines are.
        <V2EditableRow
          key={key}
          label={line.caption}
          data={line.values}
          onCellChange={(period, value) => updateInput(`pnlLines.${key}.values`, period, value)}
        />
      ))}
    </>
  );
}

export function PnLViewV2() {
  const model = useFinancialModelV2Store((s) => s.model);
  const updateInput = useFinancialModelV2Store((s) => s.updateInput);

  const handleChange = useCallback(
    (path: string) => (period: string, value: number) => updateInput(path, period, value),
    [updateInput],
  );

  if (!model) return null;
  const { inputs, computed } = model;
  const { pnl } = computed;

  return (
    <V2Sheet>
      <SectionHeader label="Revenue" />
      <V2EditableRow
        label="Sales 1"
        data={inputs.revenue.sales1}
        onCellChange={handleChange('revenue.sales1')}
      />
      <V2EditableRow
        label="Sales 2"
        data={inputs.revenue.sales2}
        onCellChange={handleChange('revenue.sales2')}
      />
      <V2EditableRow
        label="Sales 3"
        data={inputs.revenue.sales3}
        onCellChange={handleChange('revenue.sales3')}
      />
      <SubtotalRow label="Total Revenue" data={pnl.totalRevenue} />

      <SectionHeader label="Cost of Sales" />
      <ComputedDataRow label="Cost of Sales (from COGS)" data={pnl.costOfSales} />
      <GrandTotalRow label="Gross Profit" data={pnl.grossProfit} />

      <SectionHeader label="Indirect Costs" />
      <ComputedDataRow label="Staff Costs (from Staffing)" data={pnl.staffCosts} />
      <ComputedDataRow
        label="Advertising & Marketing (from Indirect costs)"
        data={pnl.advertisingMarketing}
      />
      <ComputedDataRow
        label="Administration (from Indirect costs)"
        data={pnl.administration}
      />
      <ComputedDataRow
        label="Finance costs (from Indirect costs)"
        data={pnl.financeCosts}
      />
      <DeclaredPnlRows lines={pnl.pnlLines} placement="operating" />
      <SubtotalRow label="Total Indirect Costs" data={pnl.totalIndirectCosts} />
      <GrandTotalRow label="Operating Profit" data={pnl.operatingProfit} />

      <SectionHeader label="EBITDA" />
      <V2EditableRow
        label="Interest receivable & other income"
        data={inputs.interestReceivable}
        onCellChange={handleChange('interestReceivable')}
      />
      <GrandTotalRow label="EBITDA" data={pnl.ebitda} />

      <SectionHeader label="Below EBITDA" />
      <V2EditableRow
        label="Interest payable"
        data={inputs.interestPayable}
        onCellChange={handleChange('interestPayable')}
      />
      {/* Charged on top of the figure above. IFRS 16 paragraph 49 puts the
          unwinding of a lease liability in finance costs rather than in rent,
          and a reader has to be able to tell the two apart. */}
      {/* Interest on the borrowings BALANCE, which replaces the figure above
          rather than adding to it: a filing's finance cost already contains the
          interest on its own loans. */}
      {Object.values(pnl.debtInterest ?? {}).some((v) => v) && (
        <ComputedDataRow
          label="Interest on borrowings (from the schedule)"
          data={pnl.debtInterest ?? {}}
        />
      )}
      {Object.values(pnl.leaseInterest ?? {}).some((v) => v) && (
        <ComputedDataRow
          label="Lease interest (IFRS 16)"
          data={pnl.leaseInterest ?? {}}
        />
      )}
      <ComputedDataRow label="Depreciation (from Fixed Assets)" data={pnl.depreciation} />
      <ComputedDataRow label="Amortisation (from Fixed Assets)" data={pnl.amortisation} />
      {/* Struck between operating profit and profit before tax. An
          impairment inside administration read as a year of unusually heavy
          overheads, which is exactly what it is not. */}
      <DeclaredPnlRows lines={pnl.pnlLines} placement="non_operating" />
      {/* Inputs, entered positive (impairment is deducted, the other two added). */}
      <V2EditableRow
        label="Impairment Losses"
        data={inputs.impairmentLosses ?? {}}
        onCellChange={handleChange('impairmentLosses')}
      />
      <V2EditableRow
        label="Gains on Derecognition"
        data={inputs.gainsOnDerecognition ?? {}}
        onCellChange={handleChange('gainsOnDerecognition')}
      />
      <V2EditableRow
        label="Share of Associates"
        data={inputs.shareOfAssociates ?? {}}
        onCellChange={handleChange('shareOfAssociates')}
      />
      <SubtotalRow label="Profit before Taxation" data={pnl.profitBeforeTax} />
      <ComputedDataRow label="Taxation" data={pnl.taxation} />
      {/* The charge for a year, where it is known: it replaces the calculated one for that year. */}
      <V2EditableRow
        label="Tax charge entered (replaces the calculated charge)"
        data={inputs.taxation ?? {}}
        onCellChange={handleChange('taxation')}
      />
      {/* The IAS 12 paragraph 79 split, shown only where deferred tax is being
          derived - otherwise the deferred half is nil and the current half
          repeats the line above. */}
      {Object.values(pnl.deferredTaxation ?? {}).some((v) => v) && (
        <>
          <ComputedDataRow
            label="— current tax"
            data={pnl.currentTaxation ?? {}}
          />
          <ComputedDataRow
            label="— deferred tax"
            data={pnl.deferredTaxation ?? {}}
          />
        </>
      )}
      {/* Losses still to be relieved. Shown because full relief is a
          simplification: a real regime restricts how much of a year's profit a
          loss can cover, and this is the figure that would be restricted. */}
      <ComputedDataRow
        label="Tax losses carried forward"
        data={pnl.taxLossesCarriedForward ?? {}}
      />
      <DeclaredPnlRows lines={pnl.pnlLines} placement="after_tax" />
      <V2EditableRow
        label="Discontinued Operations (after tax)"
        data={inputs.discontinuedOperations ?? {}}
        onCellChange={handleChange('discontinuedOperations')}
      />
      <GrandTotalRow label="Profit after Taxation" data={pnl.profitAfterTax} />

      {/* IAS 33 paragraph 66 wants both on the face of the statement. Shown
          only where a weighted average share count is given: a per-share figure
          over a denominator nobody stated is worse than none. */}
      {/* Always shown: the share counts are entered here, so hiding the section until a count
          existed meant one could never be entered. */}
      <SectionHeader label="Earnings per share" />
      <V2EditableRow
        label="Weighted average shares"
        data={inputs.shares?.weightedAverageShares ?? {}}
        onCellChange={handleChange('shares.weightedAverageShares')}
      />
      <V2EditableRow
        label="Dilutive shares"
        data={inputs.shares?.dilutiveShares ?? {}}
        onCellChange={handleChange('shares.dilutiveShares')}
      />
      <V2EditableRow
        label="Preference dividends"
        data={inputs.shares?.preferenceDividends ?? {}}
        onCellChange={handleChange('shares.preferenceDividends')}
      />
      {Object.values(pnl.basicEarningsPerShare ?? {}).some((v) => v) && (
        <>
          <ComputedDataRow
            label="Earnings for ordinary shareholders"
            data={pnl.earningsForOrdinaryShareholders ?? {}}
          />
          <ComputedDataRow
            label="Basic (per share)"
            data={pnl.basicEarningsPerShare ?? {}}
            format="number"
          />
          <ComputedDataRow
            label="Diluted (per share)"
            data={pnl.dilutedEarningsPerShare ?? {}}
            format="number"
          />
        </>
      )}

      {/* Outside profit or loss entirely, but inside equity - which is what
          makes it OTHER comprehensive income. Split as IAS 1 requires
          between what may yet reach earnings and what never will. */}
      <SectionHeader label="Other Comprehensive Income" />
      <V2EditableRow
        label="To be reclassified to profit or loss"
        data={inputs.ociToBeReclassified ?? {}}
        onCellChange={handleChange('ociToBeReclassified')}
      />
      <V2EditableRow
        label="Never reclassified"
        data={inputs.ociNotToBeReclassified ?? {}}
        onCellChange={handleChange('ociNotToBeReclassified')}
      />
      <GrandTotalRow
        label="Total Comprehensive Income"
        data={pnl.totalComprehensiveIncome ?? {}}
      />

      <SectionHeader label="Retained" />
      <V2EditableRow
        label="Dividends"
        data={inputs.controlAccount.dividends}
        onCellChange={handleChange('controlAccount.dividends')}
      />
      <SubtotalRow label="Profits after Dividends" data={pnl.profitsAfterDividends} />
      <V2EditableRow
        label="FRS102 adjustment"
        data={inputs.frs102Adjustment}
        onCellChange={handleChange('frs102Adjustment')}
      />
      <GrandTotalRow label="Retained Profits" data={pnl.retainedProfits} />

      {/* The Companies House proof, as Baseline Accounting and the template keep it (r64):
          what was filed, less the model. An import fills the filed figure from the filing. */}
      <SectionHeader label="Companies House proof" />
      <V2EditableRow
        label="Filed profit after dividends (Companies House)"
        data={inputs.filedProfitAfterDividends ?? {}}
        onCellChange={handleChange('filedProfitAfterDividends')}
      />
      <ComputedDataRow label="Companies House proof (filed less model)" data={pnl.chProofProfit ?? {}} />
    </V2Sheet>
  );
}
